/**
 * A workflow's runs: how many in each state, filtered by state, newest
 * first, and each run's step log, with failed steps marked.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Badge, Card, Drawer, Group, Loader, Pagination, SegmentedControl, Stack, Table, Text, ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconClock, IconDoorExit, IconFilter, IconFlag, IconPlayerSkipForward, IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  useWorkflowRun, useWorkflowRuns, type WorkflowRun, type WorkflowRunStatus, type WorkflowRunStep,
} from '../../api/workflows';
import { TOKENS } from '../../theme';
import { relativeTime } from '../../ui/format';
import { useVocab } from '../../vocab';
import { exactTime } from '../crm/Timeline';
import { LOG_STATUS, RUN_STATUS, recordWord, stepLabel } from './workflowModel';

const STATUSES: WorkflowRunStatus[] = ['running', 'waiting', 'done', 'goal_reached', 'exited', 'failed', 'cancelled'];

function RunBadge({ status }: { status: string }) {
  const s = RUN_STATUS[status] ?? { label: status, color: 'gray' };
  return <Badge variant="light" color={s.color} radius="sm" tt="none">{s.label}</Badge>;
}

/** Where a run's record is, when it has a page of its own. */
function recordPath(run: WorkflowRun): string | null {
  const id = encodeURIComponent(run.record_id);
  switch (run.record_type) {
    case 'person': return `/people/${id}`;
    case 'deal': return `/deals?deal=${id}`;
    case 'task': return '/tasks';
    default: return null;
  }
}

export function RunHistory({ workflowId }: { workflowId: string }) {
  const v = useVocab();
  const [status, setStatus] = useState<WorkflowRunStatus | null>(null);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const runs = useWorkflowRuns(workflowId, status, page);
  const counts = runs.data?.counts;
  const list = runs.data?.runs ?? [];
  const pages = runs.data?.pagination.total_pages ?? 1;

  const total = counts ? STATUSES.reduce((n, s) => n + (counts[s] ?? 0), 0) : 0;
  return (
    <Stack gap="sm" data-testid="run-history">
      <SegmentedControl
        size="xs"
        value={status ?? 'all'}
        onChange={(x) => { setStatus(x === 'all' ? null : (x as WorkflowRunStatus)); setPage(1); }}
        data={[
          { value: 'all', label: `All ${counts ? total : ''}`.trim() },
          ...STATUSES.map((s) => ({ value: s, label: `${RUN_STATUS[s]?.label ?? s} ${counts ? (counts[s] ?? 0) : ''}`.trim() })),
        ]}
        style={{ alignSelf: 'flex-start', maxWidth: '100%', overflowX: 'auto' }}
        aria-label="Filter by status"
      />
      {runs.error && <Alert color="red" variant="light">{runs.error.message}</Alert>}
      {runs.isPending && <Group justify="center" p="lg"><Loader size="sm" /></Group>}
      {!runs.isPending && list.length === 0 && (
        <Card padding="lg">
          <Text size="sm" c="dimmed" ta="center">
            {status ? `No ${RUN_STATUS[status]?.label.toLowerCase()} ${v.many('run')}.` : `No ${v.many('run')} yet. Switch the ${v.one('workflow')} on and it runs when its trigger happens.`}
          </Text>
        </Card>
      )}
      {list.length > 0 && (
        <Card padding={0}>
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Record</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Started</Table.Th>
                  <Table.Th>Step</Table.Th>
                  <Table.Th>Error</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.map((r) => (
                  <Table.Tr
                    key={r.id}
                    onClick={() => setOpen(r.id)}
                    style={{ cursor: 'pointer' }}
                    data-testid="run-row"
                    data-status={r.status}
                  >
                    <Table.Td>
                      <Text size="xs" c="dimmed">{recordWord(v, r.record_type)}</Text>
                      <Text size="sm" ff="monospace" truncate="end" maw={200}>{r.record_id}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <RunBadge status={r.status} />
                        {r.conditions_met === false && (
                          <Tooltip label="The conditions did not match, so nothing was done.">
                            <Badge variant="outline" color="gray" radius="sm" tt="none" leftSection={<IconFilter size={10} />}>Not matched</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={exactTime(r.started_at)} openDelay={300}>
                        <Text size="sm" component="time" dateTime={r.started_at}>{relativeTime(r.started_at)}</Text>
                      </Tooltip>
                      {r.status === 'waiting' && r.next_at && (
                        <Text size="xs" c="dimmed">{`Goes on ${exactTime(r.next_at)}`}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.current_step < 0 ? 'Conditions' : `${Math.min(r.current_step + 1, r.step_count)} of ${r.step_count}`}</Text>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 320 }}>
                      {r.error ? (
                        <Text size="sm" style={{ color: TOKENS.redText, overflowWrap: 'anywhere' }} lineClamp={2}>{r.error}</Text>
                      ) : (
                        <Text size="sm" c="dimmed">None</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
      {pages > 1 && <Pagination size="sm" value={page} onChange={setPage} total={pages} />}
      <RunDrawer workflowId={workflowId} runId={open} onClose={() => setOpen(null)} />
    </Stack>
  );
}

const LOG_ICONS: Record<string, typeof IconCheck> = {
  done: IconCheck,
  skipped: IconPlayerSkipForward,
  retrying: IconRefresh,
  failed: IconAlertTriangle,
  waiting: IconClock,
  goal_reached: IconFlag,
  exited: IconDoorExit,
};

function RunDrawer({ workflowId, runId, onClose }: { workflowId: string; runId: string | null; onClose: () => void }) {
  const v = useVocab();
  const detail = useWorkflowRun(workflowId, runId);
  const run = detail.data?.run;
  const path = run ? recordPath(run) : null;
  return (
    <Drawer opened={runId !== null} onClose={onClose} position="right" size="lg" title={`${v.One('run')} log`}>
      {detail.error && <Alert color="red" variant="light">{detail.error.message}</Alert>}
      {detail.isPending && runId && <Group justify="center" p="lg"><Loader size="sm" /></Group>}
      {run && detail.data && (
        <Stack gap="md" data-testid="run-log">
          <Stack gap={4}>
            <Group gap="xs" wrap="wrap">
              <RunBadge status={run.status} />
              <Text size="sm" c="dimmed">{`Version ${run.workflow_version}`}</Text>
            </Group>
            <Text size="sm">
              {`${recordWord(v, run.record_type).replace(/^./, (x) => x.toUpperCase())} `}
              {path ? (
                <Anchor component={Link} to={path} size="sm" ff="monospace" underline="always">{run.record_id}</Anchor>
              ) : (
                <Text span ff="monospace" size="sm">{run.record_id}</Text>
              )}
            </Text>
            <Text size="xs" c="dimmed">
              {`Started ${exactTime(run.started_at)}`}
              {run.finished_at ? `, finished ${exactTime(run.finished_at)}` : ''}
            </Text>
            {run.status === 'waiting' && run.next_at && (
              <Group gap={6}><IconClock size={14} /><Text size="xs">{`Goes on ${exactTime(run.next_at)}`}</Text></Group>
            )}
            {run.enrolled && <Text size="xs" c="dimmed">Enrolled from a segment.</Text>}
            {run.last_wait && (
              <Text size="xs" c="dimmed">{`Last wait: ${run.last_wait === 'matched' ? 'matched' : 'timed out'}.`}</Text>
            )}
            {run.exit_detail && (
              <Alert color={run.status === 'goal_reached' ? 'teal' : 'gray'} variant="light" p="xs" data-testid="run-exit">
                <Text size="sm">{run.exit_detail}</Text>
              </Alert>
            )}
            {run.error && (
              <Alert color="red" variant="light" p="xs"><Text size="sm">{run.error}</Text></Alert>
            )}
          </Stack>
          <Stack gap="xs">
            {detail.data.log.length === 0 && <Text size="sm" c="dimmed">Nothing has happened yet.</Text>}
            {detail.data.log.map((l, i) => <LogLine key={`${l.step_index}-${i}`} line={l} />)}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}

function LogLine({ line }: { line: WorkflowRunStep }) {
  const v = useVocab();
  const s = LOG_STATUS[line.status] ?? { label: line.status, color: 'gray' };
  const Icon = LOG_ICONS[line.status] ?? IconCheck;
  const failed = line.status === 'failed';
  const name = line.step_index === -2
    ? 'Goal'
    : line.step_index < 0
      ? 'Conditions'
      : `Step ${line.step_index + 1}: ${stepLabel(v, line.kind)}`;
  return (
    <Group
      gap="sm"
      wrap="nowrap"
      align="flex-start"
      p="xs"
      data-testid="run-log-line"
      data-status={line.status}
      style={{
        borderRadius: 8,
        border: `1px solid ${failed ? 'var(--mantine-color-red-light-color)' : TOKENS.border}`,
        background: failed ? TOKENS.redSoft : undefined,
      }}
    >
      <ThemeIcon variant="light" radius="xl" size={24} color={s.color} style={{ flex: 'none' }}>
        <Icon size={13} />
      </ThemeIcon>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Group gap={6} justify="space-between" wrap="nowrap">
          <Text size="sm" fw={600} truncate="end">{name}</Text>
          <Badge variant={failed ? 'filled' : 'light'} color={s.color} radius="sm" tt="none" style={{ flex: 'none' }}>{s.label}</Badge>
        </Group>
        {line.detail && (
          <Text size="sm" style={{ color: failed ? TOKENS.redText : TOKENS.textSecondary, overflowWrap: 'anywhere' }}>{line.detail}</Text>
        )}
        <Text size="xs" c="dimmed">{exactTime(line.at)}</Text>
      </div>
    </Group>
  );
}
